import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Heart, PawPrint, Pencil, Pill, Scissors, Trash2, Truck, User2, UtensilsCrossed } from 'lucide-react'

import { EditPetDialog } from '@/features/pet-management/ui/EditPetDialog'
import { Button } from '@/shared/ui/button'
import { Card, CardContent } from '@/shared/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Pet } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

interface PetCardProps {
    pet: Pet
    onSave?: (pet: Pet) => void
    onDelete?: (pet: Pet) => void
}

const TRANSLATIONS_FROM_PET_SIZE: Record<Pet['size'], { key: string; default: string }> = {
    pequeño: { key: 'small', default: 'Small' },
    mediano: { key: 'medium', default: 'Medium' },
    grande: { key: 'large', default: 'Large' },
}

export const PetCard = ({ pet, onSave, onDelete }: PetCardProps) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

    const { t } = useTranslation(undefined, { keyPrefix: 'pet' })

    const translateSize = (size: Pet['size']) => {
        const translation = TRANSLATIONS_FROM_PET_SIZE[size]
        return t(translation.key, translation.default)
    }

    const translateSex = (sex: 'M' | 'F') => {
        return sex === 'M' ? t('male', 'Male') : t('female', 'Female')
    }

    const handleSave = (editedPet: Pet) => {
        onSave?.(editedPet)
        setIsEditDialogOpen(false)
    }

    const handleDelete = () => {
        onDelete?.(pet)
        setIsDeleteDialogOpen(false)
    }

    const getServiceIcon = (service: AdditionalService) => {
        switch (service.type) {
            case 'driver':
                return <Truck className='h-4 w-4 text-blue-500' />
            case 'special_food':
                return <UtensilsCrossed className='h-4 w-4 text-blue-500' />
            case 'medication':
                return <Pill className='h-4 w-4 text-blue-500' />
            case 'special_care':
                return <Heart className='h-4 w-4 text-blue-500' />
            case 'hairdressing':
                return <Scissors className='h-4 w-4 text-blue-500' />
        }
    }

    const translateService = (service: AdditionalService) => {
        switch (service.type) {
            case 'driver':
                return t('driver', 'Driver')
            case 'special_food':
                return t('specialFood', 'Special food')
            case 'medication':
                return t('medication', 'Medication')
            case 'special_care':
                return t('specialCare', 'Special care')
            case 'hairdressing':
                return t('hairdressing', 'Hairdressing')
        }
    }

    return (
        <Card className='w-full bg-gray-50 shadow-md transition-shadow hover:shadow-lg'>
            <CardContent className='p-3 sm:p-4'>
                <h3 className='mb-3 text-lg font-bold text-blue-600 sm:mb-4 sm:text-xl'>{pet.name}</h3>

                <div className='grid grid-cols-1 gap-3 text-sm sm:gap-4'>
                    <div className='space-y-2 sm:space-y-3'>
                        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
                            <div className='flex items-center gap-1.5 sm:gap-2'>
                                <PawPrint className='h-4 w-4 shrink-0 text-blue-500' />
                                <span className='font-semibold'>
                                    {t('breed', 'Breed')} / {t('size', 'Size')}:
                                </span>
                            </div>
                            <span className='pl-5 sm:pl-0'>
                                {pet.breed} | {translateSize(pet.size)}
                            </span>
                        </div>
                        <div className='flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2'>
                            <div className='flex items-center gap-1.5 sm:gap-2'>
                                <User2 className='h-4 w-4 shrink-0 text-blue-500' />
                                <span className='font-semibold'>
                                    {t('sex', 'Sex')} / {t('weight', 'Weight')}:
                                </span>
                            </div>
                            <span className='pl-5 sm:pl-0'>
                                {translateSex(pet.sex || 'M')} | {pet.weight} {t('kg', 'kg')} |{' '}
                                {pet.isNeutered
                                    ? t('neutered', 'Neutered/Spayed')
                                    : t('notNeutered', 'Not neutered/spayed')}
                            </span>
                        </div>
                        {pet.additionalServices && pet.additionalServices.length > 0 && (
                            <div className='flex flex-col gap-1 sm:gap-2'>
                                <span className='font-semibold'>{t('services', 'Services')}:</span>
                                <div className='flex flex-wrap gap-2 pl-5 sm:pl-0'>
                                    {pet.additionalServices.map((service, index) => (
                                        <div key={index} className='flex items-center gap-1.5 text-sm text-gray-600'>
                                            {getServiceIcon(service)}
                                            <span>{translateService(service)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className='mt-4 flex flex-col gap-2 sm:mt-6'>
                    <Button
                        onClick={() => setIsEditDialogOpen(true)}
                        size='sm'
                        className='w-full bg-blue-500 text-white hover:bg-blue-600'
                    >
                        <Pencil className='mr-1 h-4 w-4' />
                        {t('editPet', 'Edit Pet')}
                    </Button>
                    <Button
                        variant='destructive'
                        size='sm'
                        onClick={() => setIsDeleteDialogOpen(true)}
                        className='w-full'
                    >
                        <Trash2 className='mr-1 h-4 w-4' />
                        {t('deletePet', 'Delete Pet')}
                    </Button>
                </div>
            </CardContent>

            <EditPetDialog
                pet={pet}
                isOpen={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                onSave={handleSave}
                hideObservations={true}
            />

            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className='w-[95vw] max-w-[425px] p-4 sm:p-6'>
                    <DialogHeader>
                        <DialogTitle>{t('deletePet', 'Delete Pet')}</DialogTitle>
                    </DialogHeader>
                    <p className='py-2'>{t('confirmDelete', 'Are you sure you want to delete this pet?')}</p>
                    <div className='mt-4 flex flex-col gap-2'>
                        <Button className='w-full' variant='destructive' onClick={handleDelete}>
                            {t('yes', 'Yes')}
                        </Button>
                        <Button className='w-full' variant='outline' onClick={() => setIsDeleteDialogOpen(false)}>
                            {t('no', 'No')}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </Card>
    )
}
